/**
 * React Query Hooks for GridTwinX API
 *
 * Type-safe data fetching with caching, background updates,
 * and optimistic mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

// =============================================================================
// Query Keys
// =============================================================================

export const queryKeys = {
  // Cases
  cases: ['cases'] as const,
  case: (id: string) => ['cases', id] as const,
  caseRuns: (caseId: string) => ['cases', caseId, 'runs'] as const,

  // Runs
  runs: ['runs'] as const,
  run: (id: string) => ['runs', id] as const,
  runResults: (runId: string) => ['runs', runId, 'results'] as const,

  // Tasks
  tasks: ['tasks'] as const,
  task: (id: string) => ['tasks', id] as const,

  // Live Data
  witsPrices: ['wits', 'prices'] as const,
  witsPriceHistory: (nodeCode: string) => ['wits', 'prices', nodeCode] as const,
  witsGeneration: ['wits', 'generation'] as const,
  transpowerHvdc: ['transpower', 'hvdc'] as const,
  transpowerOutages: ['transpower', 'outages'] as const,

  // Catalogue
  catalogue: ['catalogue'] as const,

  // Health
  health: ['health'] as const,
};

// =============================================================================
// Types
// =============================================================================

export interface Case {
  id: string;
  name: string;
  description?: string;
  base_mva: number;
  status: 'draft' | 'validated' | 'archived';
  created_at: string;
  updated_at: string;
  bus_count?: number;
  branch_count?: number;
  generator_count?: number;
}

export interface Run {
  id: string;
  case_id: string;
  solver_type: 'acpf' | 'dcpf' | 'acopf' | 'dcopf';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  iterations?: number;
  converged?: boolean;
}

export interface Task {
  id: string;
  type: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  message?: string;
  created_at: string;
  updated_at: string;
}

export interface SolveRequest {
  solver_type: 'dc_pf' | 'ac_pf' | 'dc_opf';
  options?: {
    max_iterations?: number;
    tolerance?: number;
    flat_start?: boolean;
  };
}

export interface NodalPrice {
  node_code: string;
  price_nzd_mwh: number;
  timestamp: string;
  trading_period: number;
  is_final: boolean;
}

// =============================================================================
// Case Hooks
// =============================================================================

export function useCases() {
  return useQuery({
    queryKey: queryKeys.cases,
    queryFn: () => api.getCases(),
    staleTime: 30_000, // 30 seconds
  });
}

export function useCase(caseId: string) {
  return useQuery({
    queryKey: queryKeys.case(caseId),
    queryFn: () => api.getCase(caseId),
    enabled: !!caseId,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Case>) => api.createCase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, data }: { caseId: string; data: Partial<Case> }) =>
      api.updateCase(caseId, data),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.case(caseId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.cases });
    },
  });
}

export function useDeleteCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId: string) => api.deleteCase(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cases });
    },
  });
}

// =============================================================================
// Solver Hooks
// =============================================================================

export function useSolveCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, request }: { caseId: string; request: SolveRequest }) =>
      api.solveCase(caseId, request),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      queryClient.invalidateQueries({ queryKey: queryKeys.caseRuns(caseId) });
    },
  });
}

export function useSolveCaseSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, request }: { caseId: string; request: SolveRequest }) =>
      api.solveCaseSync(caseId, request),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseRuns(caseId) });
    },
  });
}

// =============================================================================
// Run Hooks
// =============================================================================

export function useRuns(caseId?: string) {
  return useQuery({
    queryKey: caseId ? queryKeys.caseRuns(caseId) : queryKeys.runs,
    queryFn: () => (caseId ? api.getCaseRuns(caseId) : api.getRuns()),
    staleTime: 10_000,
  });
}

export function useRun(runId: string) {
  return useQuery({
    queryKey: queryKeys.run(runId),
    queryFn: () => api.getRun(runId),
    enabled: !!runId,
  });
}

export function useRunResults(runId: string) {
  return useQuery({
    queryKey: queryKeys.runResults(runId),
    queryFn: () => api.getRunResults(runId),
    enabled: !!runId,
  });
}

// =============================================================================
// Task Hooks
// =============================================================================

export function useTasks() {
  return useQuery({
    queryKey: queryKeys.tasks,
    queryFn: () => api.getTasks(),
    refetchInterval: 5_000, // Poll every 5 seconds for active tasks
  });
}

export function useTask(taskId: string) {
  return useQuery({
    queryKey: queryKeys.task(taskId),
    queryFn: () => api.getTask(taskId),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const task = query.state.data as Task | undefined;
      if (task?.status === 'running' || task?.status === 'pending') {
        return 2_000; // Poll faster for active tasks
      }
      return false;
    },
  });
}

export function useCancelTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => api.cancelTask(taskId),
    onSuccess: (_, taskId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },
  });
}

// =============================================================================
// Live Data Hooks
// =============================================================================

export function useWitsPrices(nodeCodes?: string[]) {
  return useQuery({
    queryKey: [...queryKeys.witsPrices, nodeCodes],
    queryFn: () => api.getWitsPrices(nodeCodes),
    staleTime: 60_000, // 1 minute
    refetchInterval: 60_000, // Refetch every minute
  });
}

export function useWitsPriceHistory(nodeCode: string, hours = 24) {
  return useQuery({
    queryKey: [...queryKeys.witsPriceHistory(nodeCode), hours],
    queryFn: () => api.getWitsPriceHistory(nodeCode, hours),
    enabled: !!nodeCode,
    staleTime: 5 * 60_000, // 5 minutes
  });
}

export function useWitsGeneration() {
  return useQuery({
    queryKey: queryKeys.witsGeneration,
    queryFn: () => api.getWitsGeneration(),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useTranspowerHvdc() {
  return useQuery({
    queryKey: queryKeys.transpowerHvdc,
    queryFn: () => api.getTranspowerHvdc(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useTranspowerOutages() {
  return useQuery({
    queryKey: queryKeys.transpowerOutages,
    queryFn: () => api.getTranspowerOutages(),
    staleTime: 5 * 60_000, // 5 minutes
  });
}

// =============================================================================
// Catalogue Hooks
// =============================================================================

export function useCatalogue() {
  return useQuery({
    queryKey: queryKeys.catalogue,
    queryFn: () => api.getCatalogue(),
    staleTime: 60_000,
  });
}

// =============================================================================
// Health Hooks
// =============================================================================

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.getHealth(),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}
